import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isUndefined } from 'lodash';

import { Dispatch } from 'src/config/rootStore';
import logoImg from 'src/public/img/mh-logo-text-modern.svg';
import {
    Button,
    ButtonGroup,
    Form,
    FormInputPassword,
    FormInputText,
    HeadingText50,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';
import { LoginFormTestId } from 'src/modules/auth/containers/testIds';
import {
    getAuthenticationErrorMessage,
    isLoginInProgress,
} from 'src/modules/auth/selectors/authenticationSelectors';
import { InternalLink } from 'src/shared/components/InternalLink';
import { MemberAccessFlow } from 'src/modules/auth/models/auth';
import { AccessFlowDetails } from 'src/modules/auth/containers/ssoOnly/LoginForm';
import { DividerWithText } from 'src/shared/components/DividerWithText';
import { config } from 'src/config/config';

interface RegistrationErrorProps {
    shouldDisplayError: boolean;
}

const ErrorContainer = styled.div<RegistrationErrorProps>(({ shouldDisplayError }) => ({
    display: shouldDisplayError ? 'inline' : 'none',
    borderRadius: pxToRem(4),
    backgroundColor: `${hexToRgba(color.cornflowerBlueDark, 0.1)}`,
    width: '100%',
}));

const ErrorMessage = styled.div(
    {
        color: `${color.blueBayoux}`,
        lineHeight: pxToRem(24),
        padding: pxToRem(12),
    },
    font.roman(font.size.medium),
);

const BrandImage = styled.img({
    marginLeft: pxToRem(32),
    marginTop: pxToRem(20),
    width: '10em',
    cursor: 'pointer',
    marginRight: pxToRem(12),
});

const FormContainer = styled.div({
    maxWidth: pxToRem(500),
    width: '100%',
});

const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: pxToRem(112),
    paddingBottom: pxToRem(14),
    justifyContent: 'center',
    flexGrow: 1,
});

const SSOButtonContainer = styled.div({
    marginBottom: pxToRem(24),
});

const SSOContainer = styled.div({
    marginBottom: pxToRem(24),
});

const RegisterLinkContainer = styled.div({
    width: '100%',
    textAlign: 'center',
});

export const getIdpFromSSOUrl = (url: string | undefined): string => {
    if (!url) return 'SSO';

    if (/okta/i.test(url)) {
        return 'Okta';
    } else if (/microsoft/i.test(url)) {
        return 'Azure';
    } else if (/google/i.test(url)) {
        return 'Google';
    } else if (/onelogin/i.test(url)) {
        return 'OneLogin';
    }

    return 'SSO';
};
interface FormData {
    email: string;
    password: string;
}

interface Props {
    accessFlowDetails: AccessFlowDetails;
    onGoBack: () => void;
}

export const LoginFlow: React.FC<Props> = ({ accessFlowDetails, onGoBack }) => {
    const { t } = useTranslation();
    const { accessFlow, email, ssoUrl } = accessFlowDetails;
    const loginFormDefinition = useForm<FormData>({
        defaultValues: {
            email,
            password: '',
        },
    });
    const loginInProgress = useSelector(isLoginInProgress);
    const errorMessage = useSelector(getAuthenticationErrorMessage);
    const dispatch = useDispatch<Dispatch>();

    const [isFocusedOnPassword, setIsFocusedOnPassword] = useState(false);

    const shouldDisplayError = !!errorMessage && errorMessage.length > 0;

    const password = loginFormDefinition.watch('password');
    const isPasswordEmpty = isUndefined(password) || password.length === 0;

    const handleSSOClick = () => {
        if (!ssoUrl) return;

        window.location.replace(ssoUrl);
    };

    const handlePasswordBlur = () => setIsFocusedOnPassword(!isPasswordEmpty);

    const handleGoBack = () => {
        dispatch.authentication.setErrorMessage('');
        onGoBack();
    };

    const handleSubmit = async () => {
        try {
            await dispatch.authentication.login({ email: accessFlowDetails.email, password });
        } catch (error) {
            dispatch.authentication.setErrorMessage(`${error}`);
        }
    };

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <Container>
                <FormContainer>
                    <HeadingText50>{t('auth:login_flow.title')}</HeadingText50>

                    {accessFlow === MemberAccessFlow.EMAIL_PW_SSO && ssoUrl && (
                        <SSOContainer>
                            <SSOButtonContainer>
                                <Button
                                    fluid
                                    onClick={handleSSOClick}
                                    size="large"
                                    variant={isFocusedOnPassword ? 'secondary' : 'primary'}
                                >
                                    {t('auth:login_flow.sso_button', {
                                        idp: getIdpFromSSOUrl(accessFlowDetails.ssoUrl),
                                    })}
                                </Button>
                            </SSOButtonContainer>

                            <DividerWithText text={t('auth:login_flow.divider')} />
                        </SSOContainer>
                    )}

                    <Form onSubmit={handleSubmit} definition={loginFormDefinition}>
                        <ErrorContainer
                            shouldDisplayError={shouldDisplayError}
                            data-testid={LoginFormTestId.ErrorMessage}
                        >
                            <ErrorMessage>{t(errorMessage)}</ErrorMessage>
                        </ErrorContainer>

                        <FormInputText
                            disabled
                            hint={t('auth:login_flow.email_hint')}
                            label={t('auth:common_form_inputs.labels.email')}
                            name="email"
                            trim
                        />

                        <FormInputPassword
                            label={t('auth:common_form_inputs.labels.password')}
                            name="password"
                            onBlur={handlePasswordBlur}
                            onFocus={() => setIsFocusedOnPassword(true)}
                        />

                        <InternalLink to="/password-reset">
                            {t('auth:authorization_box.forgot_your_password')}
                        </InternalLink>

                        <ButtonGroup>
                            <Button onClick={handleGoBack} variant="secondary" size="large">
                                {t('auth:login_form.back')}
                            </Button>

                            <Button
                                type="submit"
                                size="large"
                                busy={loginInProgress}
                                disabled={isPasswordEmpty}
                            >
                                {t('auth:login_form.continue')}
                            </Button>
                        </ButtonGroup>

                        <RegisterLinkContainer>
                            <InternalLink
                                to={{
                                    pathname: '/registration-basics',
                                    state: { email },
                                }}
                            >
                                {t('auth:login_flow.sign_up_here')}
                            </InternalLink>{' '}
                            {t('auth:login_flow.dont_have_account')}
                        </RegisterLinkContainer>
                    </Form>
                </FormContainer>
            </Container>
        </>
    );
};
